import {
  Delete as DeleteIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { FC, useEffect, useRef } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import { FormDropdown } from 'src/components/FormPart/FormDropdown';
import { FormTextInput } from 'src/components/FormPart/FormTextInput';
import { FormComponentProps } from 'src/components/FormPart/types';
import { NumberInput, SelectOption } from 'src/UIKit';
import { AutomationFormData, AutomationTriggerType } from '../../types';

// Custom contact field types returned by the API (see CreateCustomFieldModal).
type CustomFieldType = 'string' | 'number' | 'dollars' | 'date' | 'datetime';

// Groups field types into the value input they need; a reset is only needed
// when this category changes (e.g. text -> date), not for every field swap.
const valueInputKind = (type?: CustomFieldType) => {
  switch (type) {
    case 'number':
    case 'dollars':
      return 'number';
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime';
    default:
      return 'text';
  }
};

const valueValidation = { required: 'Value is required' };

// Renders the value input matching the selected custom field type and always
// stores the value as a string so validation and the save payload stay consistent.
const ActionValueInput: FC<{
  form: UseFormReturn<AutomationFormData>;
  index: number;
  fieldType?: CustomFieldType;
}> = ({ form, index, fieldType }) => {
  const fieldName = `actions.${index}.value` as const;
  const kind = valueInputKind(fieldType);

  if (kind === 'date' || kind === 'datetime') {
    const storeFormat = kind === 'datetime' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
    const Picker = kind === 'datetime' ? DateTimePicker : DatePicker;

    return (
      <Controller
        control={form.control}
        name={fieldName}
        rules={valueValidation}
        render={({ field, fieldState }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Picker
              label="Value"
              value={field.value ? dayjs(field.value) : null}
              onChange={value => field.onChange(value ? dayjs(value).format(storeFormat) : '')}
              format={kind === 'datetime' ? 'MM/DD/YYYY hh:mm A' : 'MM/DD/YYYY'}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'dense',
                  required: true,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          </LocalizationProvider>
        )}
      />
    );
  }

  if (kind === 'number') {
    return (
      <Controller
        control={form.control}
        name={fieldName}
        rules={valueValidation}
        render={({ field, fieldState }) => (
          <NumberInput
            label="Value"
            placeholder="Enter value"
            margin="dense"
            value={field.value === '' || field.value == null ? null : Number(field.value)}
            onChange={values => field.onChange(values.value)}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            required
            inputProps={
              fieldType === 'dollars'
                ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                : undefined
            }
          />
        )}
      />
    );
  }

  return (
    <FormTextInput
      form={form}
      fieldName={fieldName}
      label="Value"
      placeholder="Enter value"
      validation={valueValidation}
      margin="dense"
    />
  );
};

const ActionRow: FC<{
  form: UseFormReturn<AutomationFormData>;
  index: number;
  metadataOptions: SelectOption<string>[];
  onRemove: () => void;
}> = ({ form, index, metadataOptions, onRemove }) => {
  const metadataId = form.watch(`actions.${index}.metadataId`);
  const fieldType = metadataOptions.find(o => o.value === metadataId)?.entity as
    | CustomFieldType
    | undefined;

  // Clear the value when the input kind changes so a stale value (e.g. free
  // text left over after switching to a date field) cannot be submitted.
  const prevKind = useRef(valueInputKind(fieldType));
  useEffect(() => {
    const kind = valueInputKind(fieldType);
    if (kind !== prevKind.current) {
      prevKind.current = kind;
      form.setValue(`actions.${index}.value`, '');
    }
  }, [fieldType, form, index]);

  return (
    <Grid item xs={12}>
      <Stack direction="row" gap={1} alignItems="center">
        <Box flex={1}>
          <FormDropdown
            form={form}
            fieldName={`actions.${index}.metadataId`}
            label="Contact field"
            options={metadataOptions}
            validation={{ required: 'Contact field is required' }}
            margin="dense"
          />
        </Box>

        <Box flex={2}>
          <ActionValueInput form={form} index={index} fieldType={fieldType} />
        </Box>

        <Box whiteSpace="nowrap">
          {/* Stored as overwriteExisting; shown inverted so the default
              (overwrite) is the unchecked state. */}
          <Controller
            control={form.control}
            name={`actions.${index}.overwriteExisting`}
            render={({ field }) => (
              <FormControlLabel
                label={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    Only update if empty
                    <Tooltip
                      arrow
                      placement={'top'}
                      title="When on, this action only sets the field if the contact doesn't already have a value. When off, it overwrites the existing value."
                    >
                      <Box
                        component="span"
                        display="inline-flex"
                        color="grey.500"
                        sx={{ cursor: 'help' }}
                      >
                        <HelpOutlineIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  </Stack>
                }
                control={
                  <Checkbox
                    checked={!field.value}
                    onChange={(_, checked) => field.onChange(!checked)}
                  />
                }
              />
            )}
          />
        </Box>

        <IconButton color="error" onClick={onRemove}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Grid>
  );
};

export type AutomationFormProps = {
  groupOptions: SelectOption<string>[];
  metadataOptions: SelectOption<string>[];
  isOptionsLoading: boolean;
  onCancel: () => void;
};

const triggerTypeOptions: SelectOption<AutomationTriggerType>[] = [
  { value: 'group_joined', title: 'Group joined' },
  { value: 'contact_created', title: 'Contact created' },
];

export const AutomationForm: FC<
  FormComponentProps<AutomationFormData, AutomationFormProps>
> = props => {
  const { form, groupOptions, metadataOptions, isOptionsLoading, onCancel } =
    props;

  const values = form.watch();
  const { id } = values;

  const triggers = useFieldArray({ control: form.control, name: 'triggers' });
  const actions = useFieldArray({ control: form.control, name: 'actions' });

  const hasCustomFields = metadataOptions.length > 0;

  const isNameValid = !!values.name?.trim();
  const areTriggersValid =
    !!values.triggers?.length &&
    values.triggers.every(
      t => !!t.triggerType && (t.triggerType === 'contact_created' || !!t.targetId),
    );
  const areActionsValid =
    !!values.actions?.length &&
    values.actions.every(a => !!a.metadataId && !!a.value?.trim());
  const isFormValid = isNameValid && areTriggersValid && areActionsValid && hasCustomFields;

  const appendTrigger = () =>
    triggers.append({ triggerType: 'group_joined', targetId: '' });


  const appendAction = () =>
    actions.append({
      metadataId: '',
      value: '',
      overwriteExisting: true,
    });

  return (
    <Stack maxHeight="72vh">
      <Box flex="0 0 auto">
        <Typography variant="body2" color="text.secondary" mb={1}>
          Automatically run actions when something happens. Choose what triggers the
          automation, then the actions it should take.
        </Typography>

        <FormTextInput
          form={form}
          fieldName="name"
          label="Name"
          placeholder="Enter automation name"
          validation={{ required: 'Name is required' }}
        />
      </Box>

      {/* Scrollable triggers + actions */}
      <Box flex="1 1 auto" overflow="auto" mt={1} pr={0.5}>
        <Grid container spacing={1.5}>
          {/* Triggers */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Triggers</Typography>
            <Typography variant="caption" color="text.secondary">
              Add one or more events that start this automation. It runs whenever any of
              them fires.
            </Typography>
          </Grid>

          {triggers.fields.map((field, index) => {
            const triggerType = form.watch(`triggers.${index}.triggerType`);
            const targetOptions =  triggerType === 'group_joined'
                  ? groupOptions
                  : [];
            const needsTarget = triggerType !== 'contact_created';

            return (
              <Grid item xs={12} key={field.id}>
                <Stack direction="row" gap={1} alignItems="center">
                  <Box flex={1}>
                    <FormDropdown
                      form={form}
                      fieldName={`triggers.${index}.triggerType`}
                      label="Trigger type"
                      options={triggerTypeOptions}
                      validation={{ required: 'Trigger type is required' }}
                      margin="dense"
                    />
                  </Box>

                  <Box flex={needsTarget ? 2 : 0}>
                    {needsTarget && (
                      <FormDropdown
                        form={form}
                        fieldName={`triggers.${index}.targetId`}
                        label={'Group'}
                        options={targetOptions}
                        validation={{ required: 'Target is required' }}
                        margin="dense"
                      />
                    )}
                  </Box>

                  <IconButton color="error" onClick={() => triggers.remove(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Grid>
            );
          })}

          <Grid item xs={12} py={0}>
            <Button
              fullWidth
              variant="contained"
              onClick={appendTrigger}
            >
              Add Trigger
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Actions</Typography>
            <Typography variant="caption" color="text.secondary">
              What the automation does when it runs. All actions run in order, every time it
              fires.
            </Typography>
          </Grid>

          {!isOptionsLoading && !hasCustomFields && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Add at least one contact custom field before you can create an automation.
              </Alert>
            </Grid>
          )}

          {hasCustomFields &&
            actions.fields.map((field, index) => (
              <ActionRow
                key={field.id}
                form={form}
                index={index}
                metadataOptions={metadataOptions}
                onRemove={() => actions.remove(index)}
              />
            ))}

          {hasCustomFields && (
            <Grid item xs={12} py={0}>
              <Button fullWidth variant="contained" onClick={appendAction}>
                Add Action
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Footer */}
      <Stack direction="row" justifyContent="flex-end" gap={1} pt={1.5} flex="0 0 auto">
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>

        <Button
          type="submit"
          disabled={form.formState.isSubmitting || !isFormValid}
          variant="contained"
          color="secondary"
        >
          {id ? 'Update' : 'Create'}
        </Button>
      </Stack>
    </Stack>
  );
};
